import React, { useState, useCallback } from 'react'
import { Grid } from '@material-ui/core'
import AdminPage from '../../../admin/shared/AdminPage'
import OpalCard from '../../../../opal/OpalCard'
import InlineCardHeader from '../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import CodeBlock from '../../../../atoms/CodeBlock'
import { useParseBlueprintFromDql } from '../../../../../api/denali'
import { decipherError } from '../../../../../utils/decipherError'

function ParseDqlAnalysisTool () {
  const [dqlQuery, setDqlQuery] = useState('')
  const [parsedStructure, setParsedStructure] = useState(null)
  const [error, setError] = useState(null)

  const { mutateAsync: parseDql, isLoading: parsing } = useParseBlueprintFromDql()

  const handleParse = useCallback(async () => {
    if (!dqlQuery.trim()) return

    try {
      setError(null)
      const result = await parseDql({
        dql: dqlQuery,
        purposeCode: 'analysis', // Dummy value for analysis-only mode
        displayName: 'Analysis'
      })
      setParsedStructure(result)
    } catch (err) {
      setError(decipherError(err))
      setParsedStructure(null)
    }
  }, [dqlQuery, parseDql])

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <OpalCard size='sm'>
            <InlineCardHeader>
              DQL Query Analysis Tool
            </InlineCardHeader>

            <div style={{ padding: '16px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <p>Parse a DQL query to analyze its structure. The tool will extract tables, columns, predicates, and parameters.</p>
                </Grid>

                <Grid item xs={12}>
                  <SydLabel required label='DQL Query' helperText='Example: SELECT cs.clientId, cs.name FROM series.client_snapshot cs WHERE cs.asOfDate = :asOfDate'>
                    <SydInput
                      value={dqlQuery}
                      onChange={(e) => setDqlQuery(e.target.value)}
                      multiline
                      rows={12}
                      placeholder='SELECT ...'
                    />
                  </SydLabel>
                </Grid>

                <Grid item xs={12}>
                  {error ? <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div> : null}
                  <SydButton
                    onClick={handleParse}
                    processing={parsing}
                    disabled={!dqlQuery.trim()}
                    icon='play_arrow'
                  >
                    Analyze Query
                  </SydButton>
                </Grid>

                {parsedStructure && (
                  <>
                    <Grid item xs={12}>
                      <h3>Analysis Results</h3>
                    </Grid>

                    <Grid item xs={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <h4>Tables ({parsedStructure.purposeData?.tables?.length || 0})</h4>
                        <ul>
                          {(parsedStructure.purposeData?.tables || []).map((table, idx) => (
                            <li key={idx}>
                              <strong>{table.schema}.{table.tableCode}</strong> as {table.alias} ({table.purpose})
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <h4>Columns ({parsedStructure.purposeData?.columns?.length || 0})</h4>
                        <ul>
                          {(parsedStructure.purposeData?.columns || []).map((col, idx) => (
                            <li key={idx}>
                              <strong>{col.name}</strong> ({col.dataType}) - {col.sourceExpression}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <h4>Master Predicate</h4>
                        <p>{parsedStructure.purposeData?.masterPredicate?.expression || 'None'}</p>
                        {parsedStructure.purposeData?.masterPredicate?.requiredParams?.length > 0 && (
                          <p style={{ fontSize: '0.875rem', color: '#666' }}>
                            Required parameters: {parsedStructure.purposeData.masterPredicate.requiredParams.join(', ')}
                          </p>
                        )}
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <h4>Full Structure (JSON)</h4>
                        <CodeBlock language='json' code={JSON.stringify(parsedStructure, null, 2)} />
                      </div>
                    </Grid>
                  </>
                )}
              </Grid>
            </div>
          </OpalCard>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default ParseDqlAnalysisTool
